import { type FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  CrossSellHorizontalSlider,
  CrossSellMetadataProvider,
  CrossSellWidgetContainer,
  CrossSellWidgetMoreLink,
  CrossSellWidgetTitle,
  type CrossSellWidgetType,
  useCrossSellConfigContext,
  useCrossSellIntersection,
  useCrossSellWidgetTitle,
  useSwrCrossSellHorizontalSlider,
} from '../../../cross-sell'
import { useGlobalConfigContext } from '../../../global-config'
import { gtmDataLayerPushCswProduct } from '../../../gtm'
import { urlResolverGetSearchPage } from '../../../url-handling'
import { useCartIntermediateContext } from '../../hooks/useCartIntermediateContext'
import { cartIntermediateAccessorGetProductIdFromCartEntry } from '../../model/cartIntermediateAccessorGetProductIdFromCartEntry'

type CartIntermediateCrossSellSliderWidgetProps = {
  itemsPerPage: number
  type: CrossSellWidgetType
}

export const CartIntermediateCrossSellSliderWidget: FC<
  CartIntermediateCrossSellSliderWidgetProps
> = ({ itemsPerPage, type }) => {
  const { widgets } = useCrossSellConfigContext()
  const { entries } = useCartIntermediateContext()
  const globalConfig = useGlobalConfigContext()

  const { handleIntersection, intersected } = useCrossSellIntersection()

  const widgetId = widgets[type]?.id
  const product = entries?.at(0)
  const productId = cartIntermediateAccessorGetProductIdFromCartEntry({
    cartIntermediateEntryObject: product,
  })

  const { data, error } = useSwrCrossSellHorizontalSlider(
    intersected && widgetId && product
      ? {
          productId,
          widgetId,
        }
      : undefined,
  )
  const widgetTitle = useCrossSellWidgetTitle({
    data,
    widgetType: type,
  })

  const moreHref = useMemo(
    () =>
      data?.items
        ? urlResolverGetSearchPage(globalConfig, {
            queryParams: {
              // eslint-disable-next-line id-length -- search param
              q: data.items.map(({ id }) => id).join(','),
            },
          })
        : undefined,
    [data?.items, globalConfig],
  )

  if (!widgetId || data?.items.length === 0 || error) {
    return null
  }

  return (
    <CrossSellMetadataProvider metadata={data?.metadata}>
      <CrossSellWidgetContainer
        className="mb-5"
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
        widgetType={type}
      >
        <div className="flex flex-col justify-between tablet:flex-row tablet:items-center">
          <CrossSellWidgetTitle className="my-2 text-xl tablet:text-2xl">
            {widgetTitle}
          </CrossSellWidgetTitle>
          <CrossSellWidgetMoreLink href={moreHref}>
            <FormattedMessage id="general.label.ShowAllProducts" />
          </CrossSellWidgetMoreLink>
        </div>

        <CrossSellHorizontalSlider
          onGtmDataLayerPush={(cswProductId) => {
            gtmDataLayerPushCswProduct({
              cswProductId,
              productId: product ? product.id : null,
              widgetId,
              widgetTitle,
            })
          }}
          products={data?.items}
          productsPerPage={itemsPerPage}
        />
      </CrossSellWidgetContainer>
    </CrossSellMetadataProvider>
  )
}
